/**
 * NodeBlock — single-block renderer for the compare views.
 *
 * `DocViewer` exposes the *whole tree*, wrapped in an `<article>` with
 * doc-column max-width and font-serif typography. The compare views need to
 * render individual blocks within a 2-column grid, so we duplicate the
 * per-node switch here without the outer article.
 *
 * Visual contract is identical to `DocViewer` (M1 §6 of the visual
 * language): mb-4 paragraphs, dashed page-break separator, etc. If
 * DocViewer's per-node styling changes, mirror those edits here.
 *
 * Performance (T3): the wrapper is `React.memo`'d on default shallow
 * compare because the `node` reference is stable across decision /
 * navigation re-renders (it comes from the parsed doc tree, not from any
 * state that changes per interaction). When the parent DecidableChange is
 * skipped by its own memo, NodeBlock's reconciliation is skipped too.
 */
import { memo } from 'react';

import type { DocNode, InlineRun } from '../doc-viewer/DocViewer.types';

interface NodeBlockProps {
  node: DocNode;
}

export const NodeBlock = memo(function NodeBlock({ node }: NodeBlockProps): React.JSX.Element | null {
  switch (node.type) {
    case 'heading': {
      const isTopLevel = (node.level ?? 2) <= 1;
      const className = isTopLevel
        ? 'mb-6 mt-2 text-3xl font-semibold text-ink-900'
        : 'mb-4 mt-2 text-xl font-semibold text-ink-900';
      return (
        <h2 className={className}>
          <RunsRow runs={node.runs} />
        </h2>
      );
    }

    case 'clauseHeading':
      return (
        <h3 className="mb-4 mt-2 text-xl font-semibold text-ink-900">
          <RunsRow runs={node.runs} />
        </h3>
      );

    case 'paragraph':
      return (
        <p className="mb-4 font-serif text-md leading-relaxed text-ink-900">
          <RunsRow runs={node.runs} />
        </p>
      );

    case 'listItem':
      return (
        <p className="mb-2 ml-6 font-serif text-md leading-relaxed text-ink-900 before:mr-2 before:content-['•']">
          <RunsRow runs={node.runs} />
        </p>
      );

    case 'pageBreak': {
      const label =
        typeof node.pageIndex === 'number' ? `第 ${node.pageIndex + 1} 页` : '分页';
      return (
        <div
          className="my-6 flex select-none items-center gap-4 text-xs text-ink-400"
          aria-hidden="true"
        >
          <div className="flex-1 border-t border-ink-200" />
          <span className="font-sans">{label}</span>
          <div className="flex-1 border-t border-ink-200" />
        </div>
      );
    }

    case 'doc':
      return null;

    default: {
      const _exhaustive: never = node.type;
      void _exhaustive;
      return null;
    }
  }
});

const RunsRow = memo(function RunsRow({ runs }: { runs: InlineRun[] }): React.JSX.Element {
  return (
    <>
      {runs.map((run, idx) => {
        const classes: string[] = [];
        if (run.bold) classes.push('font-semibold');
        if (run.italic) classes.push('italic');
        if (run.underline) classes.push('underline');
        if (classes.length === 0) return <span key={idx}>{run.text}</span>;
        return (
          <span key={idx} className={classes.join(' ')}>
            {run.text}
          </span>
        );
      })}
    </>
  );
});

export default NodeBlock;
